import { createAuthenticatedApi } from '../api';
import { User, CheckAccessResponse } from '../models/user';

/**
 * Fetch all users with full details
 */
export const fetchAllUsers = async (token: string): Promise<User[]> => {
  try {
    const api = createAuthenticatedApi(token);
    const response = await api.get('/Users');
    return response.data;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

/**
 * Fetch a user by ID
 */
export const fetchUserById = async (token: string, id: number): Promise<User> => {
  try {
    const api = createAuthenticatedApi(token);
    const response = await api.get(`/Users/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user with ID ${id}:`, error);
    throw error;
  }
};

/**
 * Fetch a user by email
 */
export const fetchUserByEmail = async (token: string, email: string): Promise<User> => {
  try {
    const api = createAuthenticatedApi(token);
    const response = await api.get(`/Users/email/${email}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user with email ${email}:`, error);
    throw error;
  }
};

/**
 * Create a new user
 */
export const createUser = async (token: string, user: User): Promise<User> => {
  try {
    const api = createAuthenticatedApi(token);
    const response = await api.post('/Users', user);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update an existing user
 */
export const updateUser = async (token: string, id: number, user: User): Promise<User> => {
  try {
    const api = createAuthenticatedApi(token);
    const response = await api.put(`/Users/${id}`, user);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Delete a user
 */
export const deleteUser = async (token: string, id: number): Promise<void> => {
  try {
    const api = createAuthenticatedApi(token);
    await api.delete(`/Users/${id}`);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Fetch all admin users (where admin=true and isActive=true)
 */
export const fetchAdminUsers = async (token: string): Promise<string[]> => {
  try {
    const api = createAuthenticatedApi(token);
    const response = await api.get('/Users/admins');
    return response.data;
  } catch (error) {
    console.error('Error fetching admin users:', error);
    throw error;
  }
};

/**
 * Check if a user has access to the application by email
 */
export const checkUserAccess = async (token: string, email: string): Promise<CheckAccessResponse> => {
  try {
    const api = createAuthenticatedApi(token);
    const response = await api.get(`/Users/check-access/${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    console.error('Error checking user access:', error);
    return { valid: false, brandAirlines: [] };
  }
};
